import { useEffect, useRef, useState } from 'react';
import {
  Events,
  TimelineDataWithLoadingState,
  TimelineFetchFilters,
  TimelineProductionBatchesAndCounts,
} from '../../../../../../../interfaces/timeline';
import { getUTCFormatOfLocalTime } from '@helpers';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getMachineEventTimeline, getMachineProductionBatches } from '@reducers/machines';
import { TIMELINE_BATCH_SELECT, EMPTY_EVENTS } from '@components/machines/components/timeline/constants';
import { useRealtimeTimelineUpdates } from '@components/machines/components/timeline/hooks/useRealtimeTimelineUpdates';
import { processTimelineResponse } from '@components/machines/components/timeline/helpers/mergeTimelineEvents';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { useMultiMachineEnhancedBatches } from './useMultiMachineEnhancedBatches';
import { Machine } from '@interfaces/machines';
import { ProductionBatch } from '@interfaces/search';

interface MachineTimelineData {
  machineId: number;
  events: Events;
  productionBatchesAndCounts: TimelineDataWithLoadingState<TimelineProductionBatchesAndCounts>;
  enhancedBatchesData: TimelineDataWithLoadingState<ProductionBatch[]>;
}

interface UseMultiMachineTimelineViewModelParams {
  startTime: string;
  endTime: string;
  yearIsEnabled: boolean;
  machines: Machine[];
}

export const useMultiMachineTimelineViewModel = ({ startTime, endTime, yearIsEnabled, machines }: UseMultiMachineTimelineViewModelParams) => {
  const dispatch = useAppDispatch();

  const { timePeriod: selectedTime } = useHandleParams();

  const [machineTimelineData, setMachineTimelineData] = useState<Record<number, MachineTimelineData>>({});

  const machineTimelineDataRef = useRef(machineTimelineData);
  machineTimelineDataRef.current = machineTimelineData;

  const { fetchTimelineWithRealtime } = useRealtimeTimelineUpdates();

  const abortControllerEvents = useRef<Record<number, AbortController>>({});
  const abortControllerProductionBatches = useRef<Record<number, AbortController>>({});
  const cancelRealtimeRefs = useRef<Record<number, () => void>>({});

  useEffect(() => {
    const newMachineIds = machines.filter((m) => !machineTimelineData[m.machineId]).map((m) => m.machineId);
    if (newMachineIds.length === 0) return;

    setMachineTimelineData((prev) => {
      const updates = { ...prev };
      newMachineIds.forEach((machineId) => {
        if (!updates[machineId]) {
          updates[machineId] = {
            machineId,
            events: EMPTY_EVENTS,
            productionBatchesAndCounts: {
              loaded: false,
              data: { batches: [], counts: [] },
            },
            enhancedBatchesData: { loaded: false, data: [] },
          };
        }
      });
      return updates;
    });
  }, [machines]);

  const getTimeLineForMachine = (machineId: number, { start, end }: TimelineFetchFilters) => {
    // Update loading state for this machine
    setMachineTimelineData((prev) => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        events: EMPTY_EVENTS,
      },
    }));

    cancelRealtimeRefs.current[machineId]?.();
    if (abortControllerEvents.current[machineId]) {
      abortControllerEvents.current[machineId].abort();
    }
    abortControllerEvents.current[machineId] = new AbortController();

    const utcStart = getUTCFormatOfLocalTime(start);
    const utcEnd = getUTCFormatOfLocalTime(end);

    cancelRealtimeRefs.current[machineId] = fetchTimelineWithRealtime({
      machineId,
      from: utcStart,
      until: utcEnd,
      thunk: getMachineEventTimeline(
        { id: machineId, start: utcStart, end: utcEnd, useAsyncFlowForRealtimeQuery: true },
        abortControllerEvents.current[machineId].signal
      ),
      processResponse: processTimelineResponse,
      onInitialData: (data) => {
        setMachineTimelineData((prev) => ({
          ...prev,
          [machineId]: {
            ...prev[machineId],
            events: { loaded: true, ...data },
          },
        }));
      },
      onRealtimeData: (data) => {
        setMachineTimelineData((prev) => ({
          ...prev,
          [machineId]: {
            ...prev[machineId],
            events: { loaded: true, ...data },
          },
        }));
      },
      getCurrentEvents: () => machineTimelineDataRef.current[machineId]?.events ?? EMPTY_EVENTS,
    });
  };

  const getProductionBatchesForMachine = async (machineId: number, { start, end }: TimelineFetchFilters) => {
    // Update loading state for this machine
    setMachineTimelineData((prev) => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        productionBatchesAndCounts: { loaded: false, data: { batches: [], counts: [] } },
      },
    }));

    if (abortControllerProductionBatches.current[machineId]) {
      abortControllerProductionBatches.current[machineId].abort();
    }
    abortControllerProductionBatches.current[machineId] = new AbortController();

    try {
      const data = await dispatch(
        getMachineProductionBatches(
          {
            id: machineId,
            start: getUTCFormatOfLocalTime(start),
            end: getUTCFormatOfLocalTime(end),
            select: TIMELINE_BATCH_SELECT,
          },
          abortControllerProductionBatches.current[machineId].signal
        )
      );

      setMachineTimelineData((prev) => ({
        ...prev,
        [machineId]: {
          ...prev[machineId],
          productionBatchesAndCounts: { loaded: true, data },
        },
      }));
    } catch (error) {
      const e = error as Error;
      if (e.name !== 'AbortError') {
        console.error('Error fetching production batches for machine', machineId, error);
      }
    }
  };

  const fetchData = async (params?: { start?: string; end?: string }) => {
    const { start = startTime, end = endTime } = params || {};
    if (machines.length && (yearIsEnabled || selectedTime !== TimePeriod.YEAR)) {
      const fetchParams = { start, end };

      // Fetch data for all machines in parallel
      const fetchPromises = machines.map((machine) =>
        Promise.allSettled([getTimeLineForMachine(machine.machineId, fetchParams), getProductionBatchesForMachine(machine.machineId, fetchParams)])
      );

      const results = await Promise.allSettled(fetchPromises);

      // Log any rejected promises for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Machine ${machines[index]?.machineId} data fetch failed:`, result.reason);
        } else if (result.status === 'fulfilled') {
          // Check individual requests within each machine
          result.value.forEach((requestResult, requestIndex) => {
            if (requestResult.status === 'rejected') {
              const requestNames = ['getTimeLineForMachine', 'getProductionBatchesForMachine'];
              console.error(`Machine ${machines[index]?.machineId} - ${requestNames[requestIndex]} failed:`, requestResult.reason);
            }
          });
        }
      });
    }
  };

  // Use the new multi-machine enhanced batches hook
  const { enhancedMachineData } = useMultiMachineEnhancedBatches(machineTimelineData);

  return {
    fetchData,
    abortControllerEvents,
    abortControllerProductionBatches,
    machineTimelineData: enhancedMachineData,
    machines,
  };
};
